
import { Search, User as UserIcon, Eye, EyeOff, LogIn, LogOut, Settings, Navigation, ShieldAlert, ChevronDown, Car, Zap, Home, MapPin, Route, Mountain, X, Loader2, Terminal, Sparkles, Download, MessageSquare, Bell,Smartphone, Waves, Camera, Trees, HelpCircle, Clock, Info, Scale, Trophy, FerrisWheel, Rocket, Compass, Film } from 'lucide-react';
import { SearchResult, AppUser, MapBounds } from '../../types';
import UserAvatar from '../common/UserAvatar';
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { searchPlace } from '../../services/geocodingService';
import { CATEGORY_MAP } from '../../services/overpassService';
import { isAdminUser } from '../../services/accessControl';
import { isReplayPromoDismissed } from '../replay/ReplayPromoCard';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { Capacitor } from '@capacitor/core';

const JeepIcon = ({ size = 16, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M3 14h18l-2-4H5l-2 4z" />
    <rect x="5" y="14" width="14" height="3" />
    <circle cx="7" cy="18" r="2" />
    <circle cx="17" cy="18" r="2" />
    <path d="M19 14v-4l-2-2H7l-2 2v4" />
    <path d="M11 10v4" />
    <path d="M14 10v4" />
  </svg>
);

/**
 * Parse coordinate strings in many common formats:
 *
 * Decimal degrees:
 *   62.4115, 7.63576
 *   62.4115 7.63576
 *   62.4115; 7.63576
 *
 * DMS (degrees, minutes, seconds) — direction before or after:
 *   N 62° 24' 41.4" E 7° 38' 8.736"
 *   62° 24' 41.4" N 7° 38' 8.736" E
 *   N62°24'41.4" E7°38'8.736"
 *
 * Degrees + decimal minutes (no seconds):
 *   N 62° 24.69' E 7° 38.146'
 *   62° 24.69' N 7° 38.146' E
 *
 * UTM (zone + easting + northing) — common in Norwegian maps:
 *   32V 397454 6925855
 *   32N 397454 6925855
 *
 * Google Maps URL coordinates:
 *   @62.4115,7.63576
 *   @62.4115,7.63576,14z
 */
const parseCoordinateQuery = (query: string): { lat: number; lon: number } | null => {
  if (!query) return null;

  const s = query
    .replace(/['''`]/g, "'")
    .replace(/["""]/g, '"')
    .trim();

  const valid = (lat: number, lon: number) =>
    Number.isFinite(lat) && Number.isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180
      ? { lat, lon } : null;

  const dmsToDecimal = (deg: number, min: number, sec: number, dir: string) => {
    const abs = deg + min / 60 + sec / 3600;
    return /[SW]/i.test(dir) ? -abs : abs;
  };

  // 1. DMS with direction AFTER digits: 62°24'41.4"N 7°38'8.7"E
  const dmsPost = /(\d{1,3})\s*[°º]\s*(\d{1,2}(?:\.\d+)?)\s*[']\s*(?:(\d{1,2}(?:\.\d+)?)\s*["]\s*)?([NSEW])/gi;
  const postMatches = [...s.matchAll(dmsPost)];
  if (postMatches.length >= 2) {
    let lat: number | null = null, lon: number | null = null;
    for (const m of postMatches) {
      const dir = m[4].toUpperCase();
      const val = dmsToDecimal(Number(m[1]), Number(m[2]), m[3] ? Number(m[3]) : 0, dir);
      if (dir === 'N' || dir === 'S') lat = val;
      if (dir === 'E' || dir === 'W') lon = val;
    }
    if (lat !== null && lon !== null) return valid(lat, lon);
  }

  // 2. DMS with direction BEFORE digits: N 62° 24' 41.4" E 7° 38' 8.7"
  const dmsPre = /([NSEW])\s*(\d{1,3})\s*[°º]\s*(\d{1,2}(?:\.\d+)?)\s*[']\s*(?:(\d{1,2}(?:\.\d+)?)\s*["]\s*)?/gi;
  const preMatches = [...s.matchAll(dmsPre)];
  if (preMatches.length >= 2) {
    let lat: number | null = null, lon: number | null = null;
    for (const m of preMatches) {
      const dir = m[1].toUpperCase();
      const val = dmsToDecimal(Number(m[2]), Number(m[3]), m[4] ? Number(m[4]) : 0, dir);
      if (dir === 'N' || dir === 'S') lat = val;
      if (dir === 'E' || dir === 'W') lon = val;
    }
    if (lat !== null && lon !== null) return valid(lat, lon);
  }

  // 3. Degrees + decimal minutes without quotes: N62 24.69 E7 38.146 or 62 24.69N 7 38.146E
  const dmPre = /([NSEW])\s*(\d{1,3})\s+(\d{1,2}(?:\.\d+)?)/gi;
  const dmPreMatches = [...s.matchAll(dmPre)];
  if (dmPreMatches.length >= 2) {
    let lat: number | null = null, lon: number | null = null;
    for (const m of dmPreMatches) {
      const dir = m[1].toUpperCase();
      const val = dmsToDecimal(Number(m[2]), Number(m[3]), 0, dir);
      if (dir === 'N' || dir === 'S') lat = val;
      if (dir === 'E' || dir === 'W') lon = val;
    }
    if (lat !== null && lon !== null) return valid(lat, lon);
  }

  // 4. Google Maps URL: @62.4115,7.63576 or @62.4115,7.63576,14z
  const gmaps = s.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
  if (gmaps) {
    return valid(Number(gmaps[1]), Number(gmaps[2]));
  }

  // 5. UTM format: 32V 397454 6925855 or 32N 397454 6925855
  const utmMatch = s.match(/^(\d{1,2})\s*([C-X])\s+(\d{5,7})\s+(\d{6,8})$/i);
  if (utmMatch) {
    const zone = Number(utmMatch[1]);
    const letter = utmMatch[2].toUpperCase();
    const easting = Number(utmMatch[3]);
    const northing = Number(utmMatch[4]);
    if (zone >= 1 && zone <= 60 && easting > 100000 && easting < 999999 && northing > 0) {
      // Simplified UTM → lat/lon (good enough for Norway / northern hemisphere)
      const isNorthern = letter >= 'N';
      const n0 = isNorthern ? 0 : 10000000;
      const k0 = 0.9996;
      const e1 = (easting - 500000) / (6378137 * k0);
      const n1 = (northing - n0) / (6378137 * k0);
      const lonOrigin = (zone - 1) * 6 - 180 + 3;
      // Approximate inverse — accuracy ~50m, good enough for map navigation
      const lat = (n1 / Math.PI) * 180;
      const lon = lonOrigin + (e1 / Math.PI) * 180 / Math.cos(lat * Math.PI / 180);
      const result = valid(lat, lon);
      if (result) return result;
    }
  }

  // 6. Decimal degrees: 62.4115, 7.63576 or 62.4115 7.63576
  const normalized = s.replace(/,/g, '.');
  const decMatch = normalized.match(/^\s*(-?\d+(?:\.\d+)?)\s*[.,;\s]\s*(-?\d+(?:\.\d+)?)\s*$/);
  if (decMatch) {
    const a = Number(decMatch[1]);
    const b = Number(decMatch[2]);
    if (Number.isFinite(a) && Number.isFinite(b)) {
      if (Math.abs(a) <= 90 && Math.abs(b) <= 180) return { lat: a, lon: b };
      if (Math.abs(b) <= 90 && Math.abs(a) <= 180) return { lat: b, lon: a };
    }
  }

  return null;
};

interface HeaderProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: SearchResult[];
  onResultClick: (r: SearchResult, autoCategory?: string) => void;
  onCategoryClick: (key: string, customBounds?: MapBounds, customLabel?: string) => void;
  currentUser: AppUser | null;
  onAuthClick: () => void;
  onShowProfile: () => void;
  onLogout: () => void;
  filterUnknownSurface: boolean;
  onToggleSurfaceFilter: () => void;
  filterVerified: boolean;
  onToggleVerifiedFilter: () => void;
  onOpenAdmin?: () => void;
  adminBadgeCount?: number;
  onOpenFeedbackInbox?: () => void;
  vehicleType: 'standard' | 'offroad';
  onSetVehicleType: (type: 'standard' | 'offroad') => void;
  isDev: boolean;
  isSearching?: boolean;
  isPaidUser?: boolean;
  onOpenUpgrade?: () => void;
  onOpenNotifications?: () => void;
  unreadNotificationsCount?: number;
  onOpenInstallApp?: () => void;
  onOpenInfo?: () => void;
  onOpenLegal?: () => void;
  onOpenTierGuide?: () => void;
  testerApplicationsEnabled?: boolean;
  onOpenTesterApplication?: () => void;
  onPlanRoute?: (coords: { latitude: number; longitude: number }, name: string) => void;
  isNavigating?: boolean;
  onLogoClick?: () => void;
  isPremiumUser?: boolean;
  hasPremiumAccess?: (u: AppUser | null) => boolean;
  showSearch?: boolean;
  onOpenStickerBoard?: () => void;
  onOpenWhatsNew?: () => void;
  whatsNewUnread?: number;
  onOpenCopilot?: () => void;
  copilotEnabled?: boolean;
  userPosition?: { latitude: number; longitude: number } | null;
}

const Header: React.FC<HeaderProps> = ({
  searchQuery, setSearchQuery, searchResults, onResultClick, onCategoryClick,
  currentUser, onAuthClick, onShowProfile, onLogout,
  filterUnknownSurface, onToggleSurfaceFilter, filterVerified, onToggleVerifiedFilter, onOpenAdmin,
  adminBadgeCount,
  onOpenFeedbackInbox,
  vehicleType, onSetVehicleType, isDev, isSearching, isPaidUser, onOpenUpgrade,
  onOpenNotifications, unreadNotificationsCount, onOpenInstallApp,
  onOpenInfo, onOpenLegal, onOpenTierGuide,
  testerApplicationsEnabled, onOpenTesterApplication, onPlanRoute, isNavigating, onLogoClick,
  isPremiumUser, hasPremiumAccess, showSearch = true,
  onOpenStickerBoard,
  onOpenWhatsNew, whatsNewUnread = 0,
  onOpenCopilot, copilotEnabled = false,
  userPosition,
}) => {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const isLight = theme === 'light';

  // Tester countdown — days remaining of 30-day trial (null if not a tester or is ambassador)
  const testerDaysLeft = React.useMemo(() => {
    if (!currentUser?.isTester) return null;
    if (currentUser.tester_valid_until) return null; // ambassador
    const joinedAt = currentUser.tester_joined_at;
    if (!joinedAt) return null;
    const joinedMs = typeof joinedAt.toMillis === 'function' ? joinedAt.toMillis() : typeof joinedAt.seconds === 'number' ? joinedAt.seconds * 1000 : new Date(joinedAt).getTime();
    if (!joinedMs || isNaN(joinedMs)) return null;
    return Math.max(0, 30 - Math.floor((Date.now() - joinedMs) / (24 * 60 * 60 * 1000)));
  }, [currentUser?.isTester, currentUser?.tester_joined_at, currentUser?.tester_valid_until]);
  const [isFocused, setIsFocused] = useState(false);
  const [dropdownDismissed, setDropdownDismissed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(window.innerWidth < 768);
  const [coordinateResult, setCoordinateResult] = useState<SearchResult | null>(null);
  const [categoryMatch, setCategoryMatch] = useState<{ key: string, label: string } | null>(null);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showSearchHelp, setShowSearchHelp] = useState(false);
  const [recentSearches, setRecentSearches] = useState<SearchResult[]>(() => {
    try { return JSON.parse(localStorage.getItem('fricamp_recent_searches') || '[]'); } catch { return []; }
  });
  const isNative = Capacitor.isNativePlatform();
  const [loginVisible, setLoginVisible] = useState(!isNative);

  // Coach-mark: peker på meny-inngangen der Reisefilm/Reisetavle varig bor.
  // Vises kun ETTER at promo-kortet er lukket (som bare skjer om brukeren var
  // kvalifisert) og bare én gang per bruker.
  const [showReplayCoach, setShowReplayCoach] = useState(false);
  const replayCoachKey = `fricamp_hint_replayentry_v1_${currentUser?.uid || 'anon'}`;
  useEffect(() => {
    if (!currentUser) return;
    let timer: number | undefined;
    const maybeShow = () => {
      const seen = window.localStorage.getItem(replayCoachKey) === '1';
      if (!seen && isReplayPromoDismissed(currentUser.uid)) {
        const delay = Capacitor.isNativePlatform() ? 1200 : 600;
        timer = window.setTimeout(() => setShowReplayCoach(true), delay);
      }
    };
    maybeShow();
    window.addEventListener('fricamp:replaypromo-dismissed', maybeShow);
    return () => {
      window.removeEventListener('fricamp:replaypromo-dismissed', maybeShow);
      if (timer) window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);
  const dismissReplayCoach = () => {
    try { window.localStorage.setItem(replayCoachKey, '1'); } catch { /* ignore */ }
    setShowReplayCoach(false);
  };

  const menuRef = useRef<HTMLDivElement>(null);
  const lastSelectedName = useRef<string>("");
  const touchingResultsRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // På native: vis logg-inn-knappen først etter 10 sekunder
  useEffect(() => {
    if (!isNative || currentUser) return;
    const timer = setTimeout(() => setLoginVisible(true), 10000);
    return () => clearTimeout(timer);
  }, [isNative, currentUser]);

  const isAdmin = isAdminUser(currentUser, isDev);
  const showPremiumBadge = isPremiumUser || hasPremiumAccess?.(currentUser) || currentUser?.subscriptionPlan === 'premium';
  const parsedCoordinates = parseCoordinateQuery(searchQuery);

  const mergedResults = React.useMemo(() => {
    const out: SearchResult[] = [];
    const seen = new Set<string>();
    const push = (r: SearchResult) => {
      const key = `${(r.class || '').toLowerCase()}|${(r.display_name || '').toLowerCase()}|${parseFloat(r.lat).toFixed(3)}|${parseFloat(r.lon).toFixed(3)}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push(r);
    };
    if (coordinateResult) push(coordinateResult);
    (searchResults || []).forEach(push);
    return out;
  }, [coordinateResult, searchResults]);

  // Reset dropdown dismissal when query changes (user is actively searching)
  // But NOT when the query was set by selecting a result (lastSelectedName)
  useEffect(() => {
    if (searchQuery.trim().length >= 2 && searchQuery !== lastSelectedName.current) {
      setDropdownDismissed(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const onResize = () => setIsMobileViewport(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      (window as any).__pwaInstallPrompt = e;
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const saveRecentSearch = (r: SearchResult) => {
    setRecentSearches(prev => {
      const next = [r, ...prev.filter(s => s.display_name !== r.display_name)].slice(0, 3);
      try { localStorage.setItem('fricamp_recent_searches', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const selectRecentSearch = (r: SearchResult) => {
    lastSelectedName.current = r.display_name;
    setDropdownDismissed(true);
    setIsFocused(false);
    onResultClick(r);
    setSearchQuery(r.display_name);
    inputRef.current?.blur();
  };

  const removeRecentSearch = (name: string) => {
    setRecentSearches(prev => {
      const next = prev.filter(s => s.display_name !== name);
      try { localStorage.setItem('fricamp_recent_searches', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  useEffect(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q || q.length < 2) {
      setCategoryMatch(null);
      setCoordinateResult(null);
      return;
    }

    if (parsedCoordinates) {
      setCategoryMatch(null);
      const lat = parsedCoordinates.lat;
      const lon = parsedCoordinates.lon;
      const pad = 0.01;
      setCoordinateResult({
        display_name: `Koordinater: ${lat.toFixed(6)}, ${lon.toFixed(6)}`,
        lat: String(lat),
        lon: String(lon),
        type: 'coordinates',
        class: 'coordinates',
        boundingbox: [
          String(lat - pad),
          String(lat + pad),
          String(lon - pad),
          String(lon + pad)
        ]
      } as SearchResult);
      return;
    } else {
      setCoordinateResult(null);
    }

    const words = q.split(/\s+/);
    let foundKey: string | null = null;
    for (const word of words) { if (CATEGORY_MAP[word]) { foundKey = word; break; } }
    if (!foundKey) { foundKey = Object.keys(CATEGORY_MAP).find(k => q.startsWith(k)) || null; }

    if (foundKey) setCategoryMatch({ key: foundKey, label: i18n.t(`spot.categories.${CATEGORY_MAP[foundKey].categoryKey}`) });
    else setCategoryMatch(null);
  }, [searchQuery, parsedCoordinates, i18n]);

  const handleClearSearch = () => {
    setSearchQuery("");
    setCategoryMatch(null);
    lastSelectedName.current = "";
    setDropdownDismissed(false);
  };

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      (window as any).__pwaInstallPrompt = null;
    }
    setIsMenuOpen(false);
  };

  const getDistanceMeta = (latStr?: string, lonStr?: string) => {
    if (!userPosition || !latStr || !lonStr) return null;
    const lat = parseFloat(latStr);
    const lon = parseFloat(lonStr);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    const R = 6371;
    const dLat = (lat - userPosition.latitude) * Math.PI / 180;
    const dLon = (lon - userPosition.longitude) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(userPosition.latitude * Math.PI / 180) * Math.cos(lat * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    const km = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distLabel = km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
    const etaMin = Math.max(1, Math.round(km * 1.5 / 70 * 60));
    const etaLabel = etaMin < 60 ? `~${etaMin} min` : `~${Math.floor(etaMin / 60)}t ${String(etaMin % 60).padStart(2, '0')}m`;
    return { km, distLabel, etaLabel };
  };

  const getTypeLabel = (type: string) => {
    // If it's a known type in our translations, use it
    const translated = t(`types.${type}`);
    if (translated && translated !== `types.${type}`) {
      return translated;
    }

    const fallbacks: Record<string, string> = {
      'coordinates': 'Koordinater',
      'spot': 'Speider-lokasjon',
      'route': 'Rute'
    };
    return fallbacks[type] || t('types.place');
  };

  const getTypeIcon = (type: string) => {
    if (type === 'coordinates') return <Navigation size={14} className="text-emerald-500" />;
    if (type === 'spot') return <MapPin size={14} className="text-emerald-500" />;
    if (type === 'route') return <Route size={14} className="text-emerald-500" />;
    if (['village', 'town', 'city', 'suburb', 'hamlet', 'quarter', 'neighborhood'].includes(type)) return <Home size={14} className="text-blue-500" />;
    if (['peak', 'mountain', 'hill'].includes(type)) return <Mountain size={14} className="text-emerald-500" />;
    if (['water', 'lake', 'river', 'coastline', 'beach', 'island'].includes(type)) return <Waves size={14} className="text-blue-400" />;
    if (['theme_park', 'zoo', 'water_park', 'aquarium'].includes(type)) return <FerrisWheel size={14} className="text-pink-500" />;
    if (['attraction', 'museum', 'monument', 'historic', 'artwork'].includes(type)) return <Camera size={14} className="text-purple-500" />;
    if (['park', 'forest', 'wood', 'nature'].includes(type)) return <Trees size={14} className="text-green-600" />;
    if (['street', 'highway', 'road'].includes(type)) return <Navigation size={14} className="text-[var(--text-muted)]" />;
    return <MapPin size={14} className="text-[var(--text-muted)]" />;
  };

  // During active navigation, show only logo — everything else hidden
  if (isNavigating) {
    return (
      <div className="absolute top-0 left-0 z-sticky pointer-events-none" style={{ paddingTop: 'var(--safe-area-top, 0px)' }}>
        <div className="p-3 pointer-events-auto">
          <div className="w-10 h-10 shrink-0 rounded-xl overflow-hidden shadow-lg cursor-pointer opacity-80 hover:opacity-100 transition-opacity" onClick={() => onLogoClick ? onLogoClick() : window.location.reload()}>
            <img src="/Fricamplogo.png" className="w-full h-full object-cover scale-125" alt="Logo" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <header className="glass z-[3200] absolute top-0 left-0 right-0 w-full transition-all" style={{
      paddingTop: 'var(--safe-area-top, 0px)',
      borderBottom: 'none',
    }}>
      <div className="p-3 md:px-6 md:py-4 w-full flex items-center gap-3 md:gap-6">
        <div className="flex items-center gap-3 shrink-0 cursor-pointer" onClick={() => onLogoClick ? onLogoClick() : window.location.reload()}>
          <div className="w-10 h-10 md:w-14 md:h-14 shrink-0 rounded-xl overflow-hidden">
            <img src="/Fricamplogo.png" className="w-full h-full object-cover scale-125" alt="Logo" />
          </div>
          <div className="hidden sm:block leading-tight">
            <h1 className="text-lg font-black tracking-tight text-[var(--text-primary)]">Fricamp</h1>
            <div className="text-xs leading-none mt-0.5 opacity-90" aria-label="Norge">🇳🇴</div>
          </div>
        </div>

        {showSearch && (
        <div className="flex-1 relative max-w-2xl mx-auto">
          <div className={`relative group transition-all ${isFocused ? 'scale-[1.01]' : ''}`}>
            <div className="absolute left-4 top-1/2 -translate-y-1/2 transition-colors z-10">
              {(isLoading || isSearching) ? (
                <Loader2 size={16} className="text-brand animate-spin" />
              ) : (
                <Search className={isFocused ? 'text-brand' : 'text-[var(--text-muted)]'} size={16} />
              )}
            </div>
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onFocus={() => setIsFocused(true)}
              onBlur={() => {
                if (touchingResultsRef.current) {
                  touchingResultsRef.current = false;
                  inputRef.current?.focus();
                  return;
                }
                setTimeout(() => setIsFocused(false), 250);
              }}
              onChange={(e) => { setSearchQuery(e.target.value); setIsFocused(true); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && parsedCoordinates) {
                  e.preventDefault();
                  const lat = parsedCoordinates.lat;
                  const lon = parsedCoordinates.lon;
                  const pad = 0.01;
                  const result: SearchResult = {
                    display_name: `Koordinater: ${lat.toFixed(6)}, ${lon.toFixed(6)}`,
                    lat: String(lat),
                    lon: String(lon),
                    type: 'coordinates',
                    class: 'coordinates',
                    boundingbox: [
                      String(lat - pad),
                      String(lat + pad),
                      String(lon - pad),
                      String(lon + pad)
                    ]
                  };
                  onResultClick(result);
                  setSearchQuery(result.display_name);
                  setDropdownDismissed(true);
                }
              }}
              placeholder={t('search.placeholder_header')}
              data-search="true"
              className={`w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-2xl py-3.5 pl-11 pr-16 md:pr-24 text-[14px] md:text-sm focus:outline-none focus:border-brand/50 text-[var(--text-primary)] placeholder:text-[var(--text-muted)] transition-all shadow-inner ${isSearching ? 'ring-2 ring-emerald-500/40 border-emerald-500/40 animate-pulse bg-emerald-500/5' : ''}`}
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 z-10">
              <button
                onClick={() => setShowSearchHelp(true)}
                className="hidden md:flex p-1.5 rounded-full transition-all border bg-[var(--bg-surface-alt)] text-[var(--text-muted)] border-[var(--border-primary)] hover:text-[var(--text-secondary)]"
                title={t('search.help_title')}
                aria-label={t('search.help_title')}
              >
                <HelpCircle size={12} />
              </button>
              <button
                onClick={onToggleVerifiedFilter}
                className={`hidden sm:flex p-1.5 rounded-full transition-all border ${filterVerified ? 'bg-amber-500/20 text-amber-400 border-amber-500/50' : 'bg-[var(--bg-surface-alt)] text-[var(--text-muted)] border-[var(--border-primary)] hover:text-[var(--text-secondary)]'}`}
                title={t('filter.verified_only')}
                aria-label={t('filter.verified_only')}
                aria-pressed={filterVerified}
              >
                <i className="fa-solid fa-star text-2xs" />
              </button>
              {searchQuery && (
                <button onClick={handleClearSearch} aria-label={t("auto.layout.header.aria-label.tom_sok", "Tøm søk")} className="p-1.5 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] rounded-full text-[var(--text-muted)] transition-colors">
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Desktop dropdown — absolute inside header (works within stacking context) */}
          {(mergedResults.length > 0 || categoryMatch) && !dropdownDismissed && searchQuery.trim().length >= 2 && (
            <div
              className="hidden md:block absolute top-[calc(100%+8px)] left-0 right-0 bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.6)] z-drawer overflow-y-auto max-h-[70vh] backdrop-blur-2xl custom-scrollbar"
              onMouseDown={(e) => { e.preventDefault(); touchingResultsRef.current = true; }}
              onTouchStart={() => { touchingResultsRef.current = true; }}
            >
              {categoryMatch && (
                <div
                  onClick={() => {
                    onCategoryClick(categoryMatch.key, undefined, searchQuery);
                    setDropdownDismissed(true);
                  }}
                  className="w-full text-left px-5 py-4 bg-emerald-500/10 hover:bg-emerald-500/20 border-b border-[var(--border-secondary)] transition-colors flex items-center justify-between cursor-pointer"
                >
                  <div className="flex items-center gap-4">
                    <div className="shrink-0 p-2.5 bg-emerald-500/20 rounded-xl text-emerald-400"><Sparkles size={20} /></div>
                    <div className="min-w-0">
                      <p className="text-[12px] font-black text-[var(--text-primary)] uppercase tracking-tight truncate pr-2">
                        {t('sidebar.results_active')} "{searchQuery.toUpperCase()}"
                      </p>
                      <span className="text-2xs text-emerald-500/70 uppercase font-black tracking-widest block mt-0.5">{t('search.search_in_viewport')}</span>
                    </div>
                  </div>
                  <Navigation size={14} className="text-emerald-500" />
                </div>
              )}
              {mergedResults.map((r, i) => {
                const isCombo = categoryMatch && !searchQuery.toLowerCase().includes(r.display_name.toLowerCase());
                const isPlaceResult = !String(r.class || '').startsWith('spot:') && !String(r.class || '').startsWith('route:');
                const distMeta = getDistanceMeta(r.lat, r.lon);
                return (
                  <div key={i} onClick={() => { lastSelectedName.current = r.display_name; saveRecentSearch(r); onResultClick(r, categoryMatch?.key); setSearchQuery(isCombo ? `${categoryMatch?.label} i ${r.display_name}` : r.display_name); setDropdownDismissed(true); setIsFocused(false); inputRef.current?.blur(); }} className="w-full text-left px-5 py-3.5 hover:bg-brand/10 active:bg-brand/10 border-b border-[var(--border-primary)] transition-colors flex items-center justify-between cursor-pointer group">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className={`shrink-0 p-2.5 rounded-xl ${isCombo ? 'bg-emerald-500/20 text-emerald-400' : 'bg-[var(--bg-surface-alt)]'}`}>{isCombo ? <Sparkles size={16} /> : getTypeIcon(r.type)}</div>
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <p className="text-[12px] md:text-2xs font-bold text-[var(--text-primary)] truncate leading-tight">{isCombo ? `${i18n.t('common.search')} ${categoryMatch?.label} ${i18n.t('common.in')} ` : ''}{r.display_name}</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-2xs text-[var(--text-muted)] uppercase font-black tracking-widest">{isCombo ? i18n.t('common.combination_search') : getTypeLabel(r.type)}</span>
                          {distMeta && (
                            <span className="inline-flex items-center gap-1 text-3xs font-extrabold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 rounded-full">
                              <Navigation size={8} /> {distMeta.distLabel} <span className="opacity-70 font-normal">({distMeta.etaLabel})</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {isPlaceResult && currentUser && onPlanRoute && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onPlanRoute(
                              { latitude: parseFloat(r.lat), longitude: parseFloat(r.lon) },
                              r.display_name
                            );
                            setDropdownDismissed(true);
                            setSearchQuery('');
                            inputRef.current?.blur();
                          }}
                          className="p-2 rounded-lg hover:bg-blue-100 text-[var(--text-muted)] hover:text-blue-600 transition-colors"
                          title={i18n.t('routePlanner.plan_route_here', 'Planlegg rute hit')}
                        >
                          <Route size={14} />
                        </button>
                      )}
                      <Navigation size={12} className="text-[var(--text-muted)] group-hover:text-brand transition-colors" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Mobile dropdown — Portal to escape header stacking context */}
          {(mergedResults.length > 0 || categoryMatch) && !dropdownDismissed && searchQuery.trim().length >= 2 && isMobileViewport && createPortal(
            <>
              <div className="fixed inset-0 bg-black/40 z-[5100]" onClick={() => setDropdownDismissed(true)} />
              <div
                className="fixed left-0 right-0 z-[5200] overflow-y-auto bg-[var(--bg-surface)] backdrop-blur-2xl custom-scrollbar"
                style={{ top: 'calc(var(--safe-area-top, 0px) + 60px)', bottom: 0 }}
                onMouseDown={(e) => { e.preventDefault(); touchingResultsRef.current = true; }}
                onTouchStart={() => { touchingResultsRef.current = true; }}
              >
                {categoryMatch && (
                  <div
                    onClick={() => {
                      onCategoryClick(categoryMatch.key, undefined, searchQuery);
                      setDropdownDismissed(true);
                    }}
                    className="w-full text-left px-5 py-4 bg-emerald-500/10 hover:bg-emerald-500/20 border-b border-[var(--border-secondary)] transition-colors flex items-center justify-between cursor-pointer"
                  >
                    <div className="flex items-center gap-4">
                      <div className="shrink-0 p-2.5 bg-emerald-500/20 rounded-xl text-emerald-400"><Sparkles size={20} /></div>
                      <div className="min-w-0">
                        <p className="text-[12px] font-black text-[var(--text-primary)] uppercase tracking-tight truncate pr-2">
                          {t('sidebar.results_active')} "{searchQuery.toUpperCase()}"
                        </p>
                        <span className="text-2xs text-emerald-500/70 uppercase font-black tracking-widest block mt-0.5">{t('search.search_in_viewport')}</span>
                      </div>
                    </div>
                    <Navigation size={14} className="text-emerald-500" />
                  </div>
                )}
                {mergedResults.map((r, i) => {
                  const isCombo = categoryMatch && !searchQuery.toLowerCase().includes(r.display_name.toLowerCase());
                  const isPlaceResult = !String(r.class || '').startsWith('spot:') && !String(r.class || '').startsWith('route:');
                  const distMeta = getDistanceMeta(r.lat, r.lon);
                  return (
                    <div
                      key={i}
                      onClick={() => { lastSelectedName.current = r.display_name; saveRecentSearch(r); onResultClick(r, categoryMatch?.key); setSearchQuery(isCombo ? `${categoryMatch?.label} i ${r.display_name}` : r.display_name); setDropdownDismissed(true); setIsFocused(false); inputRef.current?.blur(); }}
                      className="w-full text-left px-5 py-3.5 active:bg-brand/10 border-b border-[var(--border-primary)] transition-colors flex items-center justify-between cursor-pointer group"
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className={`shrink-0 p-2.5 rounded-xl ${isCombo ? 'bg-emerald-500/20 text-emerald-400' : 'bg-[var(--bg-surface-alt)]'}`}>{isCombo ? <Sparkles size={16} /> : getTypeIcon(r.type)}</div>
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <p className="text-[12px] font-bold text-[var(--text-primary)] truncate leading-tight">{isCombo ? `${i18n.t('common.search')} ${categoryMatch?.label} ${i18n.t('common.in')} ` : ''}{r.display_name}</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-2xs text-[var(--text-muted)] uppercase font-black tracking-widest">{isCombo ? i18n.t('common.combination_search') : getTypeLabel(r.type)}</span>
                            {distMeta && (
                              <span className="inline-flex items-center gap-1 text-3xs font-extrabold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 rounded-full">
                                <Navigation size={8} /> {distMeta.distLabel} <span className="opacity-70 font-normal">({distMeta.etaLabel})</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {isPlaceResult && currentUser && onPlanRoute && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onPlanRoute(
                                { latitude: parseFloat(r.lat), longitude: parseFloat(r.lon) },
                                r.display_name
                              );
                              setDropdownDismissed(true);
                              setSearchQuery('');
                              inputRef.current?.blur();
                            }}
                            className="p-2 rounded-lg active:bg-blue-100 text-[var(--text-muted)] active:text-blue-600 transition-colors"
                            title={i18n.t('routePlanner.plan_route_here', 'Planlegg rute hit')}
                          >
                            <Route size={14} />
                          </button>
                        )}
                        <Navigation size={12} className="text-[var(--text-muted)] group-hover:text-brand transition-colors" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>,
            document.body
          )}

          {/* Desktop recent searches — shown when focused with empty/short query */}
          {isFocused && searchQuery.trim().length < 2 && recentSearches.length > 0 && (
            <div
              className="hidden md:block absolute top-[calc(100%+8px)] left-0 right-0 bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.6)] z-drawer overflow-hidden"
              onMouseDown={(e) => { e.preventDefault(); touchingResultsRef.current = true; }}
              onTouchStart={() => { touchingResultsRef.current = true; }}
            >
              <div className="px-4 pt-3 pb-1.5 flex items-center gap-2">
                <Clock size={12} className="text-[var(--text-muted)]" />
                <span className="text-3xs font-black uppercase tracking-widest text-[var(--text-muted)]">{t('search.recent_searches', 'Siste søk')}</span>
              </div>
              {recentSearches.map((r, i) => (
                <div
                  key={i}
                  onClick={() => selectRecentSearch(r)}
                  className="w-full text-left px-5 py-3 hover:bg-brand/10 border-b border-[var(--border-primary)] transition-colors flex items-center justify-between cursor-pointer group"
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="shrink-0 p-2 rounded-xl bg-[var(--bg-surface-alt)]">{getTypeIcon(r.type)}</div>
                    <p className="text-tiny font-bold text-[var(--text-primary)] truncate">{r.display_name}</p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeRecentSearch(r.display_name); }}
                    className="p-1 rounded-full hover:bg-[var(--bg-surface-alt)] text-[var(--text-muted)] opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Mobile recent searches — Portal */}
          {isFocused && searchQuery.trim().length < 2 && recentSearches.length > 0 && isMobileViewport && createPortal(
            <>
              <div className="fixed inset-0 bg-black/40 z-[5100]" onClick={() => setIsFocused(false)} />
              <div
                className="fixed left-0 right-0 z-[5200] bg-[var(--bg-surface)]"
                style={{ top: 'calc(var(--safe-area-top, 0px) + 60px)' }}
                onMouseDown={(e) => { e.preventDefault(); touchingResultsRef.current = true; }}
                onTouchStart={() => { touchingResultsRef.current = true; }}
              >
                <div className="px-4 pt-3 pb-1.5 flex items-center gap-2">
                  <Clock size={12} className="text-[var(--text-muted)]" />
                  <span className="text-3xs font-black uppercase tracking-widest text-[var(--text-muted)]">{t('search.recent_searches', 'Siste søk')}</span>
                </div>
                {recentSearches.map((r, i) => (
                  <div
                    key={i}
                    onClick={() => selectRecentSearch(r)}
                    className="w-full text-left px-5 py-3.5 active:bg-brand/10 border-b border-[var(--border-primary)] transition-colors flex items-center justify-between cursor-pointer"
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="shrink-0 p-2 rounded-xl bg-[var(--bg-surface-alt)]">{getTypeIcon(r.type)}</div>
                      <p className="text-[12px] font-bold text-[var(--text-primary)] truncate">{r.display_name}</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeRecentSearch(r.display_name); }}
                      className="p-1.5 rounded-full text-[var(--text-muted)]"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </>,
            document.body
          )}
        </div>
        )}

        <div className="flex items-center gap-3 shrink-0 ml-auto">
          {currentUser && onOpenNotifications && (
            <button
              onClick={onOpenNotifications}
              className="relative p-2 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-secondary)] hover:border-[var(--border-secondary)] transition-all hidden md:flex"
              title={t("auto.layout.header.title.varsler", "Varsler")}
              aria-label={t("auto.layout.header.aria-label.varsler", "Varsler")}
            >
              <Bell size={16} className="text-[var(--text-secondary)]" />
              {(unreadNotificationsCount ?? 0) > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-3xs font-black text-white flex items-center justify-center">
                  {Math.min(unreadNotificationsCount!, 9)}
                </span>
              )}
            </button>
          )}
          {currentUser ? (
          <div className="relative" ref={menuRef}>
            {showReplayCoach && !isMenuOpen && (
              <div className="absolute top-[calc(100%+8px)] right-0 z-[3102] w-56 animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="absolute -top-1.5 right-6 w-3 h-3 rotate-45 bg-brand border-l border-t border-brand/40" />
                <div className="relative rounded-xl bg-brand text-stone-900 px-3 py-2.5 shadow-[0_12px_30px_rgba(0,0,0,0.35)]">
                  <div className="flex items-start gap-2">
                    <Film size={15} className="mt-0.5 flex-shrink-0" />
                    <p className="text-2xs font-black leading-snug">
                      {t('replayPromo.coachmark', 'Reisefilmen og reisetavla di finner du her')}
                    </p>
                    <button
                      type="button"
                      onClick={dismissReplayCoach}
                      aria-label={t('replayPromo.dismiss', 'Skjul')}
                      className="flex-shrink-0 -mt-0.5 -mr-1 p-1 rounded-md hover:bg-black/10 transition-colors"
                    >
                      <X size={13} />
                    </button>
                  </div>
                </div>
              </div>
            )}
            <button onClick={() => { if (showReplayCoach) dismissReplayCoach(); setIsMenuOpen(!isMenuOpen); }} className={`relative flex items-center gap-2 p-1 pr-3 rounded-xl border transition-all duration-300 ${isMenuOpen ? 'bg-emerald-500/10 border-emerald-500/50' : 'bg-[var(--bg-surface)] border-[var(--border-secondary)] hover:border-[var(--border-secondary)]'}`}>
              <UserAvatar
                photoURL={currentUser?.photoURL}
                tier={currentUser?.tier}
                size="sm"
              />
              {testerDaysLeft !== null && testerDaysLeft <= 15 && (
                <span
                  className={`absolute -bottom-1 -left-1 min-w-[16px] h-4 px-1 rounded-full text-3xs font-black text-white flex items-center justify-center ${
                    testerDaysLeft <= 5 ? 'bg-red-500' : testerDaysLeft <= 10 ? 'bg-amber-500' : 'bg-emerald-500'
                  }`}
                  title={t('account.tester_days_left', '{{days}} dager igjen', { days: testerDaysLeft })}
                >
                  {testerDaysLeft}d
                </span>
              )}
              {(unreadNotificationsCount ?? 0) > 0 && (
                <span className="md:hidden absolute -top-1 -right-1 min-w-[16px] h-4 px-0.5 bg-red-500 rounded-full text-3xs font-black text-white flex items-center justify-center">
                  {Math.min(unreadNotificationsCount!, 9)}
                </span>
              )}
              <ChevronDown size={14} className={`text-[var(--text-muted)] transition-transform duration-300 ${isMenuOpen ? 'rotate-180 text-emerald-500' : ''}`} />
            </button>
            {isMenuOpen && (
              <div className="absolute top-[calc(100%+8px)] right-0 w-64 bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] z-[3101] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="p-4 border-b border-[var(--border-primary)] bg-[var(--bg-input)]">
                  <p className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest">{t('account.title')}</p>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs font-bold text-[var(--text-primary)] truncate">{currentUser?.displayName || t('account.guest')}</p>
                    {showPremiumBadge ? (
                      <span className="bg-amber-500/20 text-amber-300 text-4xs font-black uppercase tracking-widest px-1.5 py-0.5 rounded border border-amber-500/30 flex items-center gap-1">
                        <Sparkles size={8} fill="currentColor" /> Premium
                      </span>
                    ) : isPaidUser ? (
                      currentUser?.isTester ? (
                        <span className="bg-emerald-500/20 text-emerald-400 text-4xs font-black uppercase tracking-widest px-1.5 py-0.5 rounded border border-emerald-500/30 flex items-center gap-1">
                          <Zap size={8} fill="currentColor" /> {t('account.tester')}{testerDaysLeft !== null && ` · ${testerDaysLeft}d`}
                        </span>
                      ) : (
                        <span className="bg-blue-500/20 text-blue-400 text-4xs font-black uppercase tracking-widest px-1.5 py-0.5 rounded border border-blue-500/30 flex items-center gap-1">
                          <Zap size={8} fill="currentColor" /> {t('account.supporter')}
                        </span>
                      )
                    ) : currentUser && (
                      <button onClick={() => { onOpenUpgrade?.(); setIsMenuOpen(false); }} className="bg-amber-500/20 text-amber-500 text-4xs font-black uppercase tracking-widest px-1.5 py-0.5 rounded border border-amber-500/30 hover:bg-amber-500/30 transition-colors">
                        {t('account.upgrade')}
                      </button>
                    )}
                  </div>
                </div>
                <div className="py-2">
                  {/* «Hva er nytt» er en tier-fordel: kun Eventyrer (nivå 3) eller høyere (+ admin).
                      Den offentlige feeden på landingssiden er fortsatt åpen for alle. */}
                  {onOpenWhatsNew && ((currentUser?.tier ?? 0) >= 3 || isAdmin) && (
                    <button onClick={() => { onOpenWhatsNew(); setIsMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-emerald-500/10 text-2xs font-black uppercase text-[var(--text-secondary)] transition-colors">
                      <Rocket size={14} className="text-emerald-500" /> {t('whatsNew.menuLabel', 'Hva er nytt')}
                      {whatsNewUnread > 0 && (
                        <span className="ml-auto px-1.5 py-0.5 rounded-full bg-emerald-500 text-white text-4xs font-black min-w-[16px] text-center">{Math.min(whatsNewUnread, 9)}</span>
                      )}
                    </button>
                  )}
                  {/* Reise-copilot — testfunksjon, kun admin/testere (copilotEnabled) */}
                  {onOpenCopilot && copilotEnabled && (
                    <button onClick={() => { onOpenCopilot(); setIsMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-emerald-500/10 text-2xs font-black uppercase text-[var(--text-secondary)] transition-colors">
                      <Compass size={14} className="text-emerald-500" /> {t('copilot.menuLabel', 'Reise-copilot')}
                      <span className="ml-auto px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-500 text-4xs font-black">TEST</span>
                    </button>
                  )}
                  {currentUser ? (
                    <>
                      {onOpenNotifications && (
                        <button onClick={() => { onOpenNotifications(); setIsMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--bg-surface-alt)] text-2xs font-black uppercase text-[var(--text-secondary)] transition-colors md:hidden">
                          <Bell size={14} /> {t('account.notifications', 'Varsler')}
                          {(unreadNotificationsCount ?? 0) > 0 && (
                            <span className="ml-auto px-1.5 py-0.5 rounded-full bg-red-500 text-white text-4xs font-black min-w-[16px] text-center">{Math.min(unreadNotificationsCount!, 9)}</span>
                          )}
                        </button>
                      )}
                      <button onClick={() => { onShowProfile(); setIsMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--bg-surface-alt)] text-2xs font-black uppercase text-[var(--text-secondary)] transition-colors ${onOpenNotifications ? 'border-t border-[var(--border-primary)] md:border-t-0' : ''}`}><Settings size={14} /> {t('account.profile')}</button>
                      {onOpenStickerBoard && (
                        <button
                          onClick={() => { onOpenStickerBoard(); setIsMenuOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--bg-surface-alt)] text-2xs font-black uppercase text-[var(--text-secondary)] transition-colors border-t border-[var(--border-primary)]"
                        >
                          <Sparkles size={14} className="text-emerald-500" /> {t('account.stickerBoard', 'Reisetavle')}
                        </button>
                      )}
                      <button
                        onClick={() => { onOpenFeedbackInbox?.(); setIsMenuOpen(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-emerald-500/10 text-2xs font-black uppercase text-emerald-300 transition-colors border-t border-[var(--border-primary)]"
                      >
                        <MessageSquare size={14} /> {t('account.feedback')}
                      </button>
                      {onOpenInfo && (
                        <button onClick={() => { onOpenInfo(); setIsMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--bg-surface-alt)] text-2xs font-black uppercase text-[var(--text-secondary)] transition-colors border-t border-[var(--border-primary)]">
                          <Info size={14} className="text-emerald-500" /> {t('map.menu.legend')}
                        </button>
                      )}
                      {onOpenLegal && (
                        <button onClick={() => { onOpenLegal(); setIsMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--bg-surface-alt)] text-2xs font-black uppercase text-[var(--text-secondary)] transition-colors border-t border-[var(--border-primary)]">
                          <Scale size={14} className="text-amber-500" /> {t('map.menu.legal')}
                        </button>
                      )}
                      {onOpenTierGuide && (
                        <button onClick={() => { onOpenTierGuide(); setIsMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--bg-surface-alt)] text-2xs font-black uppercase text-[var(--text-secondary)] transition-colors border-t border-[var(--border-primary)]">
                          <Trophy size={14} className="text-yellow-500" /> {t('map.menu.levels')}
                        </button>
                      )}
                      {!isPaidUser && !currentUser?.isTester && (
                        testerApplicationsEnabled && onOpenTesterApplication ? (
                          <button onClick={() => { onOpenTesterApplication(); setIsMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-amber-500/10 text-2xs font-black uppercase text-amber-400 transition-colors border-t border-[var(--border-primary)]"><Sparkles size={14} /> {t('upgrade.tester.btn_apply')}</button>
                        ) : (
                          <div className="w-full flex items-center gap-3 px-4 py-3 text-2xs font-black uppercase text-[var(--text-muted)] border-t border-[var(--border-primary)]"><Sparkles size={14} /> {t('upgrade.tester.not_recruiting')}</div>
                        )
                      )}

                      {isAdmin && !isMobileViewport && (
                        <button onClick={() => { onOpenAdmin?.(); setIsMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-emerald-500/10 text-2xs font-black uppercase text-emerald-400 transition-colors border-t border-[var(--border-primary)] relative">
                          <ShieldAlert size={14} /> {t('account.admin')}
                          {!!adminBadgeCount && adminBadgeCount > 0 && (
                            <span className="ml-auto px-1.5 py-0.5 rounded-full bg-red-500 text-white text-4xs font-black min-w-[16px] text-center">{adminBadgeCount}</span>
                          )}
                        </button>
                      )}


                      {!isNative && onOpenInstallApp && (
                        <button onClick={() => { onOpenInstallApp(); setIsMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-emerald-500/10 text-2xs font-black uppercase text-emerald-400 transition-colors border-t border-[var(--border-primary)]"><Smartphone size={14} /> {t('install_app.menu_label')}</button>
                      )}

                      <button onClick={() => { onLogout(); setIsMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-500/10 text-2xs font-black uppercase text-red-400 transition-colors border-t border-[var(--border-primary)]"><LogOut size={14} /> {t('account.logout')}</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => { onAuthClick(); setIsMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-4 hover:bg-emerald-500/10 text-2xs font-black uppercase text-brand transition-colors"><UserIcon size={16} /> {t('account.login')}</button>
                      {!isNative && (deferredPrompt || /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) && (
                        <button 
                          onClick={deferredPrompt ? handleInstallClick : () => { onOpenInstallApp?.(); setIsMenuOpen(false); }} 
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-emerald-500/10 text-2xs font-black uppercase text-emerald-400 transition-colors border-t border-[var(--border-primary)]"
                        >
                          <Download size={14} /> {t('account.install')}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          ) : (
          <div className={`flex items-center gap-2 ${loginVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'} transition-all duration-500`}>
            {/* Install/download button intentionally omitted while logged out — it
                squeezed the search field on narrow phones and the install action
                stays available in the menu after login. */}
            <button
              onClick={onAuthClick}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-500/15 border border-emerald-500/30 hover:bg-emerald-500/25 hover:border-emerald-500/50 transition-all text-emerald-400 text-2xs font-black uppercase tracking-wider"
            >
              <LogIn size={14} />
              {t('account.login')}
            </button>
          </div>
          )}
        </div>
      </div>

      {showSearchHelp && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" onClick={() => setShowSearchHelp(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="relative bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.6)] w-full max-w-lg max-h-[85vh] overflow-y-auto custom-scrollbar"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-[var(--bg-surface)] border-b border-[var(--border-primary)] px-5 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <h2 className="text-sm font-black uppercase tracking-wider text-[var(--text-primary)]">{t('search.help_title')}</h2>
              <button onClick={() => setShowSearchHelp(false)} className="p-1.5 rounded-full hover:bg-[var(--bg-surface-alt)] text-[var(--text-muted)] transition-colors">
                <X size={16} />
              </button>
            </div>

            <div className="px-5 py-4 space-y-5">
              {/* Spots & routes */}
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <MapPin size={14} className="text-emerald-400 shrink-0" />
                  <h3 className="text-tiny font-black uppercase tracking-wider text-[var(--text-primary)]">{t('search.help_spots_title')}</h3>
                </div>
                <p className="text-tiny text-[var(--text-secondary)] leading-relaxed">{t('search.help_spots_desc')}</p>
              </div>

              {/* Places & addresses */}
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <Home size={14} className="text-blue-400 shrink-0" />
                  <h3 className="text-tiny font-black uppercase tracking-wider text-[var(--text-primary)]">{t('search.help_places_title')}</h3>
                </div>
                <p className="text-tiny text-[var(--text-secondary)] leading-relaxed">{t('search.help_places_desc')}</p>
              </div>

              {/* Category search */}
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <Search size={14} className="text-purple-400 shrink-0" />
                  <h3 className="text-tiny font-black uppercase tracking-wider text-[var(--text-primary)]">{t('search.help_categories_title')}</h3>
                </div>
                <p className="text-tiny text-[var(--text-secondary)] leading-relaxed mb-2">{t('search.help_categories_desc')}</p>
                <div className="bg-[var(--bg-input)] border border-[var(--border-primary)] rounded-xl px-3 py-2.5">
                  <div className="flex flex-wrap gap-1.5">
                    {t('search.help_categories_list').split(', ').map((cat) => (
                      <span key={cat} className="px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-300 text-3xs font-bold border border-purple-500/20">{cat}</span>
                    ))}
                  </div>
                </div>
                <p className="text-2xs text-[var(--text-muted)] mt-2 leading-relaxed">{t('search.help_categories_combo')}</p>
              </div>

              {/* Coordinates */}
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <Navigation size={14} className="text-cyan-400 shrink-0" />
                  <h3 className="text-tiny font-black uppercase tracking-wider text-[var(--text-primary)]">{t('search.help_coordinates_title')}</h3>
                </div>
                <p className="text-tiny text-[var(--text-secondary)] leading-relaxed mb-2">{t('search.help_coordinates_desc')}</p>
                <div className="bg-[var(--bg-input)] border border-[var(--border-primary)] rounded-xl px-3 py-2.5">
                  <pre className="text-2xs text-[var(--text-secondary)] font-mono leading-relaxed whitespace-pre-wrap">{t('search.help_coordinates_formats')}</pre>
                </div>
              </div>

              {/* Star filter */}
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <i className="fa-solid fa-star text-[12px] text-amber-400 shrink-0" />
                  <h3 className="text-tiny font-black uppercase tracking-wider text-[var(--text-primary)]">{t('search.help_star_title')}</h3>
                </div>
                <p className="text-tiny text-[var(--text-secondary)] leading-relaxed">{t('search.help_star_desc')}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default React.memo(Header);
